/**
 * Track 2: public, shareable battle report.
 *
 * A shared replay must reveal nothing private: no civilization / fleet / ship uuids, no player-chosen ship names,
 * no system id. Only what happened on the field is kept – ship class, side, hull / shield values and the
 * event stream – with ship ids replaced by stable slots ("a1", "d3") and names generated from class + slot.
 * Pure and deterministic, so the same battle always produces the same public replay (and it is unit-testable).
 */
import { z } from 'zod';
import type { BattleEvent, BattleReplay } from './types/combat.js';

const CLASS_LABEL: Record<string, string> = {
  corvette: 'Korvett', frigate: 'Fregatt', cruiser: 'Cirkáló', scout: 'Felderítő', construction: 'Építő',
};

export const PublicReplayShipSchema = z.object({
  id: z.string().regex(/^[ad]\d{1,3}$/),
  name: z.string().max(40),
  shipClass: z.enum(['corvette', 'frigate', 'cruiser', 'scout', 'construction']),
  maxHull: z.number().int(),
  maxShield: z.number().int(),
  armor: z.number().int(),
  side: z.enum(['attacker', 'defender']),
});
export type PublicReplayShip = z.infer<typeof PublicReplayShipSchema>;

export const PublicBattleEventSchema = z.object({
  tick: z.number().int(),
  type: z.enum(['attack', 'shield_break', 'ship_destroyed', 'battle_end', 'retreat_started']),
  attackerId: z.string().optional(),
  targetId: z.string().optional(),
  attackerName: z.string().optional(),
  targetName: z.string().optional(),
  shieldDamage: z.number().int().optional(),
  hullDamage: z.number().int().optional(),
  message: z.string(),
});

export const PublicBattleReplaySchema = z.object({
  winner: z.enum(['attacker', 'defender', 'draw']).nullable(),
  ships: z.array(PublicReplayShipSchema),
  log: z.array(PublicBattleEventSchema),
});
export type PublicBattleReplay = z.infer<typeof PublicBattleReplaySchema>;

/** The unguessable share id: 16 random bytes as base64url (22 chars). Validated before any DB lookup. */
export const SHARE_TOKEN_RE = /^[A-Za-z0-9_-]{22}$/;
export function isShareToken(s: string): boolean { return SHARE_TOKEN_RE.test(s); }

export function toPublicReplay(replay: Pick<BattleReplay, 'replayData' | 'log' | 'winner'>): PublicBattleReplay {
  const slot = new Map<string, { id: string; name: string }>();
  const counters = { attacker: 0, defender: 0 };
  const ships: PublicReplayShip[] = replay.replayData.ships.map(s => {
    const n = ++counters[s.side];
    const id = `${s.side === 'attacker' ? 'a' : 'd'}${n}`;
    const name = `${CLASS_LABEL[s.shipClass] ?? 'Hajó'} ${id.toUpperCase()}`;
    slot.set(s.id, { id, name });
    return { id, name, shipClass: s.shipClass, maxHull: s.maxHull, maxShield: s.maxShield, armor: s.armor, side: s.side };
  });

  const log = replay.log.map((ev): z.infer<typeof PublicBattleEventSchema> => {
    const a = ev.attackerId ? slot.get(ev.attackerId) : undefined;
    const t = ev.targetId ? slot.get(ev.targetId) : undefined;
    const out: z.infer<typeof PublicBattleEventSchema> = { tick: ev.tick, type: ev.type, message: publicMessage(ev, a?.name, t?.name) };
    if (a) { out.attackerId = a.id; out.attackerName = a.name; }
    if (t) { out.targetId = t.id; out.targetName = t.name; }
    if (ev.shieldDamage !== undefined) out.shieldDamage = ev.shieldDamage;
    if (ev.hullDamage !== undefined) out.hullDamage = ev.hullDamage;
    return out;
  });
  return { winner: replay.winner, ships, log };
}

/** Messages are rebuilt from public names; the original text may contain real ship names. */
function publicMessage(ev: BattleEvent, attacker?: string, target?: string): string {
  switch (ev.type) {
    case 'attack': return `${attacker ?? '?'} → ${target ?? '?'}: pajzs −${ev.shieldDamage ?? 0}, törzs −${ev.hullDamage ?? 0}`;
    case 'shield_break': return `${target ?? '?'} pajzsa megsemmisült!`;
    case 'ship_destroyed': return `${target ?? '?'} megsemmisült!`;
    // battle_end / retreat_started texts only name the sides, never a ship
    default: return ev.message;
  }
}

/** Adapter so the replay player can run on public data with the same engine-facing shape. */
export function publicToBattleReplay(p: PublicBattleReplay): BattleReplay {
  return {
    battleId: '00000000-0000-0000-0000-000000000000',
    replayData: {
      attackerFleetId: '00000000-0000-0000-0000-000000000000',
      defenderFleetId: '00000000-0000-0000-0000-000000000000',
      ships: p.ships.map(s => ({ ...s, id: s.id as string })) as BattleReplay['replayData']['ships'],
    },
    log: p.log,
    winner: p.winner,
  };
}
